import React, { useState, useRef, useEffect } from "react";
import { FaTimes, FaEdit, FaPlus, FaTrash, FaEllipsisV, FaCheck, FaPrint, FaSignInAlt, FaCalendarAlt, FaClock, FaBed, FaBolt, FaDoorOpen, FaDoorClosed, FaBell } from "react-icons/fa";
import type BookedRoom from "../../models/BookedRoom";
import { attachCurrentTime, formatDate, formatTime } from "../../utils/date";
import AddEditBookingModal from "./AddEditBookingModal";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import Room from "../../models/Room";
import { initAllStores } from "../../utils/cache";
import Booking from "../../models/Booking";
import { capitalizeFirst, padNumber } from "../../utils/string";
import FullPageSpinner from "../../components/FullPageSpinner";
import { BookingUi } from "../../types/booking";
import { calculateBillCharge, clearBill, generatedBillNo, numberToWords } from "../../utils/bill";
import { sendRequest, useHandleApiConflict } from "../../utils/api";
import Bill from "../accounting/BillModal";
import Hotel from "../../models/Hotel";
import User from "../../models/User";
import { BillData } from "../../types/bill";
import ExtendStayModal from "./ExtendStayModal";
// import { checkRoomAvailability, } from "../utils/booking-helper";
import { checkAccess, computeAccess, computeBookingOrderAccess } from "../../utils/access";
import { checkRoomAvailability, getBookingReceiptSummary } from "../../utils/booking-helper";
import { DELETED_STAFF } from "../../constants";
import { ReceiptData } from "../../types/print-setting";
import ReceiptModal from "../accounting/ReceiptModal";
import { getUserHotel } from "../../utils/user-helper";
import { terminal } from "../../utils/user-helper";
import NotifyGuestModal from "../NotifyGuest";
import { syncServices } from "../../utils/sync";
// import { toast } from "react-toastify";


interface BookingDetailsModalProps {
    show: boolean;
    onClose: () => void;
    booking: Booking & { totalBeforeDiscount: number };
    setBookings: React.Dispatch<React.SetStateAction<BookingUi[]>>;
    bookedRooms: BookedRoom[];
    syncBooking: () => void;
    // onAddRoom: () => void;
}

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
    show,
    onClose,
    booking,
    bookedRooms,
    setBookings,
    syncBooking,
    // onAddRoom,
}) => {
    const { openModal, closeModal } = useModal();
    const { showToast } = useToast();

    const [terminalId] = useState(() => terminal());

    const { handleApiConflict } = useHandleApiConflict({
        onReload: () => syncBooking(),
    });

    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [bookedRoomsUi, setBookedRoomsUi] = useState<BookedRoom[]>(bookedRooms);
    const [bookingCostSummary, setBookingCostSummary] = useState({
        discount: booking.discount,
        totalBeforeDiscount: booking.totalBeforeDiscount,
        allPaid: bookedRooms.every((bookedRoom) => bookedRoom.isPaid),
        totalPaid: bookedRooms
            .filter((r: BookedRoom) => r.isPaid)
            .reduce((sum, r) => sum + r.amount - r.discount, 0),
    });

    const [bookingBill, setBookingBill] = useState<BillData>({
        issuedBy: "",
        bookings: [],
        discount: 0,
        hotel: {
            id: "",
            name: "",
            email: "",
            phone: "",
            address: "",
            preference: { vatRate: 0, serviceCharge: 0 } as any
        },
        guestName: "",
        billNumber: ""
    });

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [bookingData, setBookingData] = useState<any>({});

    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptData, setReceiptData] = useState<ReceiptData>({} as ReceiptData);

    const [loading, setLoading] = useState(false);

    const [showExtendModal, setShowExtendModal] = useState(false);

    const [isEdit, setIsEdit] = useState(false);
    const [access, setAccess] = useState({
        // notifyGuest: false,
        updateBooking: false,
        forceDeleteBooking: false,
    });
    const [showBill, setShowBill] = useState(false);

    const [hotel, setHotel] = useState<Hotel>({} as Hotel);
    const [user, setUser] = useState({} as any);

    const [open, setOpen] = useState(false);
    const [guestName, setGuestName] = useState("Guest");
    const [room, setRoom] = useState({} as any);

    useEffect(() => {
        async function starterData() {
            const data = await getUserHotel();
            if (data) {
                const { hotel, user } = data;
                setHotel(hotel);
                setUser(user);
            } else {
                return openModal(
                    "Session Expired ⚠️",
                    "We couldn’t find your user information. Please log in again to continue.",
                    () => {
                        closeModal();
                        window.location.href = "/login";
                    },
                    false,
                    "Login Again"
                );
            }

        }
        starterData();
    }, []);

    useEffect(() => {
        const accessGranted = computeBookingOrderAccess("bookings");
        setAccess(accessGranted);
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeMenu) {
                const menuEl = menuRefs.current[activeMenu];
                if (menuEl && !menuEl.contains(event.target as Node)) {
                    setActiveMenu(null);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [activeMenu]);

    if (!show || !hotel || !user) return null;

    const getBookings = async (
        bookingId: string
    ): Promise<Booking> => {
        const cache = await initAllStores();
        const booking: Booking | null = await cache.getBy("bookings", { id: bookingId });
        if (!booking) {
            openModal(
                "Session Expired ⚠️",
                "We couldn’t retrieve the booking details because your session data is out of sync."
                + " Please log in again so we can refresh your account information.",
                () => {
                    closeModal();
                    window.location.href = "/login";
                },
                false,
                "Log In Again"
            );
            throw new Error("Booking Not Found.");
        }
        return booking;
    }

    function calculateTotals(
        bookings: { amount?: number; discount?: number; isPaid?: boolean }[] = []
    ) {
        const { totalAmount, totalDiscount, totalPaid } = bookings.reduce(
            (acc, room) => {
                const amount = room.amount || 0;
                const discount = room.discount || 0;

                acc.totalAmount += amount;
                acc.totalDiscount += discount;

                if (room.isPaid) {
                    acc.totalPaid += amount - discount;
                }

                return acc;
            },
            { totalAmount: 0, totalDiscount: 0, totalPaid: 0 }
        );

        const isAllPaid = bookings.length > 0 && bookings.every(b => b.isPaid);

        return {
            totalAmount,
            totalDiscount,
            totalPaid,
            isAllPaid,
        };
    }

    const validateAction = (
        // isPaid: boolean,
        isUse: boolean,
        bookingType: string,
        extraText: string = ""
    ) => {
        if (!isUse && bookingType !== "reservation") {
            openModal(
                "Action Not Allowed ⚠️",
                `This booking is no longer in use${extraText ? ` and cannot be ${extraText}.` : "."}`,
                closeModal,
                false,
                "Close"
            );
            return true;
        }

        return false;
    };

    const confirmCheckIn = (
        roomNumber: string,
        checkInDate: Date,
        checkOutDate: Date,
        proceedAction: () => void,
    ) => {
        const today = new Date();

        // ⛔ Before reservation period
        if (today.setHours(0, 0, 0, 0) < checkInDate.setHours(0, 0, 0, 0)) {
            return openModal(
                "Check-In Not Allowed ⚠️",
                `Room ${roomNumber} reservation starts on ${checkInDate.toDateString()}. Guests can only be checked in on or after this date.
                Would you like to adjust the reservation date instead?`,
                () => {
                    closeModal();
                    setShowUpdateModal(true);
                    setBookingData({
                        ...bookingData,
                        guestName: bookingData.guestName,
                        // bookingNo: `BK-${padNumber(Number(booking.bookingNo))}`,
                        guestNumber: bookingData.guestNumber ?? "",
                    });
                },
                true, "Adjust Date", "Cancel"
            );
        }

        // ⛔ After reservation period
        if (today.setHours(0, 0, 0, 0) > checkOutDate.setHours(0, 0, 0, 0)) {
            return openModal(
                "Reservation Period Ended ⚠️",
                `Room ${roomNumber} reservation ended on ${checkOutDate.toDateString()}. You can no longer check in this guest.
      
                Would you like to extend the booking period?`,
                () => {
                    closeModal();
                    // call extend booking logic here
                },
                true,
                "Extend Booking",
                "Cancel"
            );
        }

        // ✅ Within valid reservation period — confirm action
        return openModal(
            "Confirm Guest Check-In",
            `You’re about to check in the guest for Room ${roomNumber} (${checkInDate.toDateString()} → ${checkOutDate.toDateString()}).
    
            Do you want to continue?`,
            () => {
                closeModal();
                proceedAction(); // actual check-in logic here
            },
            true,
            "Yes, Proceed",
            "Cancel"
        );
    };

    const addBooking = async (book: BookedRoom) => {
        setLoading(true); // show spinner immediately

        openModal(
            "Add Room to Booking",
            "Are you sure you want to add this room to the booking record?",
            async () => {
                try {
                    // Initialize cache and user
                    const cache = await initAllStores();
                    const bookedRooms = await cache.getAllBy(
                        "bookedRooms", { roomId: book.roomId, isUse: true }
                    );
                    // Fetch all room data in parallel

                    const conflictMsg = await checkRoomAvailability(
                        bookedRooms,
                        book.roomId,
                        book.checkInDate,
                        book.checkOutDate
                    );

                    if (conflictMsg) {
                        setLoading(false);
                        return openModal(
                            "Room Conflict ⚠️",
                            conflictMsg,
                            () => closeModal(),
                            false,
                            "Close"
                        );
                    }

                    const booking: Booking | null = await cache.getBy("bookings", { id: book.bookingId });
                    if (!booking) throw new Error("Booking not found.");

                    const { customer, bill, bookingNo, ...restBookingData } = booking;
                    // Prepare main booking objects\
                    const bookingData = {
                        guestData: customer,
                        billData: bill,
                        bookingData: {

                            ...restBookingData,
                            clearedById:
                                book.isPaid ? user.userId : null
                        }
                    };

                    // console.log(book.checkInDate)
                    const enrichedBookData = {
                        ...book, isUse: true, terminal: terminalId,
                        //   checkInDate: attachCurrentTime(book.checkInDate),
                    };

                    if (enrichedBookData.roomNumber) delete enrichedBookData.roomNumber;

                    // Send booking request
                    await sendRequest("POST", "/rooms/book", {
                        ...bookingData, bookedRoomData: [enrichedBookData]
                    });

                    // Update cache in parallel
                    const { permissions, userId, ...rest } = user;
                    const checkedInBy = { ...rest, id: user.id };
                    const clearedBy = book.isPaid ? { ...rest, id: user.id } : null;

                    enrichedBookData.checkedInBy = checkedInBy;
                    enrichedBookData.clearedBy = clearedBy;

                    enrichedBookData.checkedInById = userId;
                    enrichedBookData.clearedById = book.isPaid ? user.id : null;

                    await Promise.all([
                        cache.updateManyBy("rooms", [book.roomId], { isOccupied: true }),
                        cache.saveOne("bookedRooms", enrichedBookData),
                    ]);

                    setShowUpdateModal(false);

                    // Retrieved updated bookings from cache.
                    const allBookings: BookedRoom[] | null = await cache.getAllBy("bookedRooms", { bookingId: booking.id });
                    const { totalAmount, totalDiscount, totalPaid, isAllPaid } = calculateTotals(allBookings);

                    setBookings((prevBookings) =>
                        prevBookings.map((b: BookingUi) =>
                            b.id === book.bookingId
                                ? {
                                    ...b,
                                    discount: totalDiscount.toLocaleString(),
                                    paymentStatus: isAllPaid ? "Paid" : "Pending",
                                    amount: totalAmount.toLocaleString(),
                                    // staffDisplayName(b).checkedIn
                                }
                                : b
                        )
                    );

                    setBookedRoomsUi((prev) => [...prev, enrichedBookData]);

                    setBookingCostSummary({
                        discount: totalDiscount,
                        totalBeforeDiscount: totalAmount,
                        allPaid: isAllPaid, totalPaid,
                    });

                    // Success modal
                    openModal(
                        `"Room Added Successfully",`,
                        `Room ${book.roomNumber} has been successfully added to the booking record.`,
                        () => {

                            if (!book.isPaid) {
                                const billNumber = generatedBillNo("BL", bill.billNo);

                                setBookingBill({
                                    issuedBy: user.firstName + " " + user.lastName,
                                    discount: book.discount,
                                    bookings: [enrichedBookData],
                                    hotel, guestName: customer?.name, billNumber
                                });
                                setShowBill(true);
                            } else {
                                const items = [{
                                    amount: book.amount.toLocaleString(),
                                    desc: `Room ${book.roomNumber?.toUpperCase() ?? ""} Accomodation`,
                                }];

                                const total = book.amount - book.discount;
                                // const {
                                //     vatCharge, serviceCharge,
                                // } = await calculateBillCharge(total, discount);
                                const data = {
                                    // receiptNo: string;
                                    guest: booking.customer?.name ?? "Deleted Customer", items,
                                    total: total.toLocaleString(),
                                    // vat: vatCharge.toLocaleString(),
                                    discount: book.discount?.toLocaleString(),
                                    paymentMethod: book.paymentMethod,
                                    reference: booking.refNo,
                                    amountInWords: numberToWords(total),
                                    address: hotel.address,
                                    // serviceCharge,
                                    phone: hotel.phone,
                                    email: hotel.email,
                                    website: hotel.website,
                                    issuedBy: user.firstName + " " + user.lastName,
                                };
                                setReceiptData(data);
                                setShowReceiptModal(true);
                            }
                        },
                        true,
                        book.isPaid ? "View Receipt" : "View Bill",
                        "Close"
                    );

                } catch (error: any) {

                    if (error.status === 409) {
                        openModal(
                            "Booking Conflict ⚠️",
                            // err.response.data.message ||
                            "This room has just been booked by another staff. Please refresh to see the latest availability.",
                            async () => {
                                // await syncServices(["rooms"]);
                                // navigate(-1);
                            },
                            false,
                            "Refresh"
                        );
                    }
                    showToast("An error occured. Please try again.", { type: "error" });

                    console.error(error);
                } finally {
                    setLoading(false); // hide spinner
                }
            },
            true,
            "Yes, Add Room",
            "Cancel"
        );
        setLoading(false);
    }

    const updateBooking = async (book: BookedRoom, room: { new: Partial<Room>, old: Partial<Room> }) => {
        openModal(
            "Confirm Booking Update",
            "Are you sure you want to update the details for this room booking?",
            async () => {
                const cache = await initAllStores();
                try {
                    const booking = await getBookings(book.bookingId);

                    if (room.new.id !== room.old.id) {
                        if (room.new.isOccupied) {
                            return openModal(
                                "Room Occupied ⚠️",
                                `Room "${room.new.number}" is currently occupied by another guest. Please try another room.`,
                                () => { }, false, "Close"
                            );
                        }
                    }

                    await sendRequest("PUT", `/bookings/${book.id}/update`, {
                        booking: book,
                        room: { newId: room.new.id, oldId: room.old.id },
                    });

                    await cache.updateManyBy(
                        "rooms", [room.new.id as string], {
                        isOccupied: book.bookingType === "fullTime" ? true : false,
                    })

                    // ✅ Save updated booking back to cache
                    await cache.updateManyBy("bookedRooms", [book.id], book);

                    // Change room status to available if no booking attached.
                    const oldRoomBookings = await cache.getAllBy("bookedRooms", { roomId: room.old.id });
                    if (oldRoomBookings.length === 0) {
                        await cache.updateManyBy("rooms", [room.old.id as string], { isOccupied: false })
                    }

                    setShowUpdateModal(false);

                    // Retrieved updated bookings from cache.
                    const allBookings: BookedRoom[] | null = await cache.getAllBy("bookedRooms", { bookingId: booking.id });
                    const { totalAmount, totalDiscount, totalPaid, isAllPaid } = calculateTotals(allBookings);

                    setBookings((prevBookings) =>
                        prevBookings.map((b: BookingUi) =>
                            b.id === booking.id
                                ? {
                                    ...b,
                                    discount: totalDiscount.toLocaleString(),
                                    amount: totalAmount.toLocaleString(),
                                    ...isEdit
                                        ? {} : { paymentStatus: isAllPaid ? "Paid" : "Pending" },
                                }
                                : b
                        )
                    );

                    setBookedRoomsUi((prev) =>
                        prev.map((b: BookedRoom) =>
                            b.id === book.id
                                ? {
                                    ...b,
                                    discount: book.discount,
                                    checkInDate: book.checkInDate,
                                    checkOutDate: book.checkOutDate,
                                    guestName: book.guestName,
                                    guestNumber: book.guestNumber,
                                    roomNumber: room.new.number,
                                    bookingType: book.bookingType,
                                    paymentMethod: book.paymentMethod,
                                    ...isEdit ? {} : { isPaid: book.isPaid },
                                }
                                : b
                        ));

                    setBookingCostSummary({
                        discount: totalDiscount, totalPaid,
                        totalBeforeDiscount: totalAmount,
                        allPaid: isAllPaid,

                    });

                    // ✅ Show success modal after all updates
                    showToast("Booking and room details have been successfully updated!", { type: "success" });
                    // onClose();
                } catch (error) {
                    const handled = handleApiConflict(error, "booking");
                    if (!handled) {
                        showToast(
                            "Something went wrong while clearing this bill. Please try again.",
                            { type: "error" }
                        );
                    }
                    onClose();
                }
                finally {
                    setLoading(false);
                }
            },
            true,
            "Yes, Update",
            "Cancel"
        );
    };

    const loadBookingBill = async () => {
        setLoading(true);
        try {
            const cache = await initAllStores();

            if (bookingCostSummary.allPaid) {

                const discount = bookingCostSummary.discount;
                const total = bookingCostSummary.totalPaid;
                // console.log(bookedRoomsUi)
                const bookedRoomIds = bookedRoomsUi.map(b => b.roomId);

                const allRooms = await cache.getAllBy("rooms", {});
                const selectedRooms = allRooms.filter((r: any) => bookedRoomIds.includes(r.id));

                const bookingReceiptSummary = getBookingReceiptSummary(
                    bookedRoomsUi, selectedRooms
                );

                const items = bookingReceiptSummary.map(r => ({
                    amount: r.totalAmount.toLocaleString(),
                    desc: `${r.quantity} x ${capitalizeFirst(r.roomName)}`,
                }));

                const {
                    vatCharge, serviceCharge,
                } = await calculateBillCharge(total, discount);
                const data = {
                    // receiptNo: string;
                    guest: booking.customer?.name ?? "Deleted Customer", items,
                    total: total.toLocaleString(),
                    amountInWords: numberToWords(total),
                    vat: vatCharge.toLocaleString(),
                    discount: discount?.toLocaleString(),
                    // paymentMethod, discount: discount,
                    reference: booking.refNo,
                    address: hotel.address,
                    serviceCharge,
                    phone: hotel.phone,
                    email: hotel.email,
                    website: hotel.website,
                    issuedBy: user.firstName + " " + user.lastName,
                };
                setReceiptData(data);
                setShowReceiptModal(true);
            } else {
                setBookingBill({
                    issuedBy: user.firstName + " " + user.lastName,
                    discount: booking.discount,
                    bookings: bookedRoomsUi ?? [],
                    hotel, guestName: booking?.customer?.name,
                    billNumber: generatedBillNo("BK", booking?.bill?.billNo)
                });
                setShowBill(true);
            }

        } catch (error: any) {
        } finally {
            setLoading(false);
        }
    }

    const confirmClearBill = (r: BookedRoom) => {
        openModal(
            "Confirm Booking Bill Clearance 🧾",
            `Are you sure you want to clear this bill for ${r.guestName || "this guest"}?`
            + " Once cleared, it cannot be modified.",
            async () => {
                const cache = await initAllStores();
                try {
                    closeModal();
                    setLoading(true);

                    const user = await cache.getBy("data", { id: "user" });
                    if (!user) {
                        return openModal(
                            "Session Expired ⚠️",
                            "We couldn’t find your user information. Please log in again to continue.",
                            () => {
                                closeModal();
                                window.location.href = "/login";
                            },
                            false,
                            "Login Again"
                        );
                    }

                    const { userId, id, ...rest } = user;

                    await Promise.all([
                        clearBill("bookedRoom", r.id),
                        cache.updateManyBy(
                            "bookedRooms", [r.id], {
                            isPaid: true, clearedById: userId,
                            clearedBy: { ...rest, id: userId }
                        })
                    ]);

                    const booking = await getBookings(r.bookingId);
                    const allBookings: BookedRoom[] | null = await cache
                        .getAllBy("bookedRooms", { bookingId: booking.id });

                    const { totalAmount, totalDiscount, totalPaid, isAllPaid } = calculateTotals(allBookings);
                    setBookings((prevBookings) =>
                        prevBookings.map((b: BookingUi) =>
                            b.id === booking.id
                                ? {
                                    ...b,
                                    paymentStatus: isAllPaid ? "Paid" : "Pending"
                                }
                                : b
                        )
                    );

                    setBookedRoomsUi((prev) =>
                        prev.map((b: BookedRoom) =>
                            b.id === r.id
                                ? {
                                    ...b,
                                    isPaid: true,
                                    clearedBy: {
                                        id: user.userId, portfolio: user.portfolio,
                                        firstName: user.firstName, lastName: user.lastName
                                    } as User,
                                }
                                : b
                        ));

                    setBookingCostSummary({
                        discount: totalDiscount,
                        totalBeforeDiscount: totalAmount,
                        allPaid: isAllPaid, totalPaid
                    });
                    setShowUpdateModal(false);

                    showToast("Bill Cleared Successfully!", { type: "success" });
                    // onClose();
                } catch (error: any) {
                    const handled = handleApiConflict(error, "booking");
                    if (!handled) {
                        showToast(
                            "Something went wrong while clearing this bill. Please try again.",
                            { type: "error" }
                        );
                    }
                    onClose();
                } finally {
                    setLoading(false);
                }
            },
            true, // ✅ Show confirmation style with Cancel / Continue
            "Yes, Clear Bill",
            "Cancel",
        );
    };

    const deleteBooking = (book: BookedRoom) => {
        openModal(
            "Confirm Bill Deletion ⚠️",
            `Are you sure you want to delete this bill for ${book.guestName || "this guest"}? `
            + "This action is permanent and cannot be undone.",
            async () => {
                const cache = await initAllStores();
                try {
                    closeModal();
                    setLoading(true);

                    await sendRequest("DELETE", `/bookings/${book.id}/delete`);
                    await cache.deleteOne("bookedRooms", book.id);
                    showToast(
                        `The booking bill for ${book.guestName || "this guest"} has been permanently deleted.`,
                        { type: "success" }
                    );

                    // Update cache data on delete.
                    const booking = await getBookings(book.bookingId);
                    const allBookings: BookedRoom[] | null = await cache
                        .getAllBy("bookedRooms", { bookingId: booking.id });

                    if (allBookings.length === 0) {
                        await cache.deleteOne("bookings", booking.id);

                        setBookings((prevBookings) =>
                            prevBookings.filter((b: BookingUi) => b.id !== booking.id)
                        );

                        onClose();
                    }


                    await cache.updateManyBy("rooms", [book.roomId], { isOccupied: false });

                    const {
                        totalAmount, totalDiscount,
                        totalPaid, isAllPaid
                    } = calculateTotals(allBookings);

                    setBookings((prevBookings) =>
                        prevBookings.map((b: BookingUi) => ({
                            ...b,
                            amount: totalAmount.toLocaleString(),
                            discount: totalDiscount.toLocaleString()
                        }))
                    );

                    setBookedRoomsUi((prev) =>
                        prev.filter((b) => b.id !== book.id)
                    );

                    setBookingCostSummary({
                        discount: totalDiscount,
                        totalBeforeDiscount: totalAmount,
                        allPaid: isAllPaid, totalPaid,
                    });

                    setShowUpdateModal(false);
                    // onClose();
                } catch (error: any) {
                    const handled = handleApiConflict(error, "booking");
                    if (!handled) {
                        showToast(
                            `Failed to delete ${book.guestName || "the guest’s"} bill. Please try again.`,
                            { type: "error" }
                        );
                    }
                    onClose();
                } finally {
                    setLoading(false);
                }
            },
            true, // ✅ Show confirmation style with Cancel / Continue
            "Yes, Delete",
            "Cancel", "red"
        );
    }

    const checkInGuest = async (bookingId: string, roomId: string) => {
        setLoading(true);
        try {
            const cache = await initAllStores();

            await Promise.all([
                sendRequest("POST", `/bookings/${bookingId}/checkin`),
                cache.updateManyBy("bookedRooms", [bookingId], {
                    bookingType: "fullTime", checkedInById: user.userId,
                    checkedInBy: {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        portfolio: user.porfolio
                    }
                }),
                cache.updateManyBy("rooms", [roomId], { bookingType: "fullTime" }),
            ]);
            showToast("Guest checked in successfully!", { type: "success" });

            setBookedRoomsUi((prev) =>
                prev.map((b: BookedRoom) =>
                    b.id === bookingId
                        ? {
                            ...b,
                            bookingType: "fullTime",
                        }
                        : b
                ));
        } catch (error: any) {
            console.log(error)
            if (error.status === 409) {
                return openModal(
                    "Booking Conflict ⚠️",
                    error?.response?.data?.error,
                    () => {
                        closeModal();
                        // window.location.href = "/login";
                    },
                    false,
                    "Close"
                );
            }
            const handled = handleApiConflict(error, "booking");
            if (!handled) {
                showToast("Check-in failed. Please try again.",
                    { type: "error" }
                );
            }
            onClose();
        } finally {
            setLoading(false);
        }
    }

    const staffDisplayName = (
        booking: BookedRoom
    ): { checkedIn: string; checkedOut: string; clearedBy: string } => {

        const getName = (user?: User | null) => {
            if (booking.isPaidByGuest) return ` (By: Guest)`;
            else if (!user) return null;
            else return ` (By: ${user.firstName} ${user.lastName})`;
        };


        // if (booking.isPaidByGuest && booking.isPaid) {
        //     return {};
        // }
        const deleteStaff = ` (By: ${DELETED_STAFF})`;

        const userDisplayName = {
            checkedIn:
                getName(booking.checkedInBy) ?? deleteStaff,

            checkedOut:
                !booking.isUse
                    ? getName(booking.checkoutBy) ?? deleteStaff : "",

            clearedBy: booking.isPaidByGuest && booking.isPaid && !user.clearedBy
                ? " (By: Guest)" : (
                    booking.isPaid
                        ? getName(booking.clearedBy) ?? deleteStaff : ""
                ),
        };

        return userDisplayName;
    };

    const createNotification = async (data: {
        message: string;
        type: "notification" | "banner" | "message"
    }) => {
        setLoading(true);

        try {
            if (data.type === "banner") {
                await sendRequest(
                    "PUT", `/rooms/${room.id}/update`, {
                    number: room.number,
                    headline: data.message,
                });

                await syncServices(["rooms"], {});
                showToast("Headline banner highlight sent successfully", { type: "success" });
            } else {
                openModal(
                    "Banner Available 📢",
                    "For now, only the Banner feature is active. Please use it to send important updates to guests. Notification and Message features will be available soon.",
                    closeModal,
                    false,
                    "Continue"
                );
            }

            setBookedRoomsUi((prev) =>
                prev.map((b: BookedRoom) =>
                    b.id === room.bookingId
                        ? {
                            ...b,
                            headline: data.message,
                        }
                        : b
                ));
            // showToast("Headline banner highlight sent successfully", { type: "success" });
        } catch (err: any) {
            showToast("Something went wrong. Please try again.", { type: "error" });
        } finally {
            setLoading(false);
        }
        // setNotifications((prev) => [data, ...prev]); // store locally for preview
    };


    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-2 sm:px-0">
            {showUpdateModal && (
                <AddEditBookingModal
                    show={showUpdateModal}
                    booking={bookingData}
                    isEdit={isEdit}
                    onClose={() => setShowUpdateModal(false)}
                    onSave={isEdit ? updateBooking : addBooking}
                />
            )}

            {/* Modal */}
            <NotifyGuestModal
                isOpen={open}
                guestName={guestName}
                onClose={() => setOpen(false)}
                onSubmit={createNotification}
                headline={room.headline}
            />

            {showReceiptModal && (
                <ReceiptModal
                    sample={receiptData}
                    onClose={() => setShowReceiptModal(false)}
                />
            )}

            {showExtendModal && (
                <ExtendStayModal
                    guest={bookingData}
                    onClose={() => setShowExtendModal(false)}
                    onExtend={(newBooking: BookedRoom) => {

                        const updatedBookings = [...bookedRoomsUi, newBooking];
                        const {
                            totalAmount, totalDiscount, totalPaid, isAllPaid
                        } = calculateTotals(updatedBookings);

                        setBookedRoomsUi(updatedBookings);

                        setBookings((prevBookings) =>
                            prevBookings.map((b: BookingUi) =>
                                b.id === booking.id
                                    ? {
                                        ...b,
                                        amount: totalAmount.toLocaleString(),
                                        discount: totalDiscount.toLocaleString(),
                                        paymentStatus: isAllPaid ? "Paid" : "Pending",
                                    }
                                    : b
                            )
                        );

                        setBookingCostSummary({
                            discount: totalDiscount,
                            totalBeforeDiscount: totalAmount,
                            allPaid: isAllPaid, totalPaid
                        });

                    }}
                />
            )}

            {/* Loader */}
            <FullPageSpinner show={loading} />

            {/* Bill Modal */}
            {showBill && (
                <Bill
                    billNumber={bookingBill.billNumber}
                    hotel={bookingBill.hotel}
                    discount={bookingBill.discount}
                    guestName={bookingBill.guestName}
                    issuedBy={bookingBill.issuedBy}
                    bookings={bookingBill.bookings}
                    onClose={() => setShowBill(false)}
                />
            )}

            <div
                className="bg-white w-full max-w-xl max-h-[90vh] rounded-2xl shadow-lg overflow-hidden animate-fadeIn relative flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
                    <h2 className="text-lg font-semibold text-center">
                        Booking #{padNumber(booking.bookingNo)} — {booking.customer?.name}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        {FaTimes({ size: 18 })}
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1">
                    {/* Booking Info */}
                    <div className="px-6 py-3 text-sm text-gray-700 border-b">
                        <div className="flex justify-between flex-wrap gap-2">
                            <p>
                                {/* <strong>Book By:</strong>
                                {" " + booking.} */}
                            </p>
                            <p>
                                <strong>Created:</strong> {formatDate(booking.createdAt)}
                            </p>
                        </div>
                    </div>

                    {/* Room Details */}
                    <div className="px-6 py-4 border-b overflow-visible relative">
                        <h3 className="font-medium mb-3">Room Booked Details</h3>

                        <div className="divide-y border rounded-lg overflow-visible relative">
                            {bookedRoomsUi.map((r: BookedRoom) => (
                                <div
                                    key={r.id}
                                    className={`flex justify-between items-center px-4 py-3 transition relative ${r.isUse ? "bg-gray-50" : "hover:bg-gray-50"}`}
                                >
                                    <div key={r.id} className="flex justify-between items-center transition relative">
                                        <div>
                                            <p className="font-semibold text-gray-900 flex items-center gap-2">
                                                {/* Icon based on booking type */}
                                                {r.bookingType.toLocaleLowerCase() === "reservation" && (
                                                    <span className="text-blue-500 flex items-center gap-1">
                                                        {FaCalendarAlt({})} <span className="text-sm">Reserve</span>
                                                    </span>
                                                )}
                                                {r.bookingType.toLocaleLowerCase() === "earlycheckin" && (
                                                    <span className="text-orange-500 flex items-center gap-1">
                                                        {FaClock({})} <span className="text-sm">Early Check-In</span>
                                                    </span>
                                                )}
                                                {r.bookingType.toLocaleLowerCase() === "fulltime" && (
                                                    <span className="text-green-600 flex items-center gap-1">
                                                        {FaBed({})} <span className="text-sm">FullTime</span>
                                                    </span>
                                                )}

                                                {r.bookingType.toLocaleLowerCase() === "shorttime" && (
                                                    <span className="text-purple-500 flex items-center gap-1">
                                                        {FaBolt({})} <span className="text-sm">Short Time</span>
                                                    </span>
                                                )}
                                                {r.bookingType.toLocaleLowerCase() === "latecheckout" && (
                                                    <span className="text-red-500 flex items-center gap-1">
                                                        {FaClock({})} <span className="text-sm">LateCheckout</span>
                                                    </span>
                                                )}

                                                {/* Room and guest info */}
                                                <span title={r.guestName}>
                                                    Room {r.roomNumber?.toUpperCase()} — {" "}
                                                    {
                                                        r.guestName.length > 10
                                                            ? r.guestName.slice(0, 10) + "..."
                                                            : r.guestName
                                                    }
                                                </span>

                                                {/* Current usage indicator */}
                                                {r.isUse ? (
                                                    <span className="ml-2 text-green-600 text-xs font-medium flex items-center gap-1">
                                                        {FaDoorOpen({})} In Use
                                                    </span>
                                                ) : (
                                                    <span className="ml-2 text-gray-400 text-xs font-medium flex items-center gap-1">
                                                        {FaDoorClosed({})} Not In Use
                                                    </span>
                                                )}
                                            </p>

                                            <p className="text-sm text-gray-600">
                                                <b>Book Time:</b> {formatDate(r.createdAt)}, {formatTime(r.checkInDate)}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                <b>Check-in:</b> {formatDate(r.checkInDate.toString())}
                                                {staffDisplayName(r).checkedIn}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                <b>Check-out:</b> {formatDate(r.checkOutDate.toString())}
                                                {staffDisplayName(r).checkedOut}
                                            </p>
                                            {r.isPaid && <p className="text-sm text-gray-600">
                                                <b>Bill Cleared</b>
                                                {staffDisplayName(r).clearedBy}
                                            </p>}
                                            <p className={`text-sm font-medium mt-1 ${r.isPaid ? "text-green-600" : "text-red-600"}`}>
                                                {r.isPaid ? "Paid" : "Unpaid"} /{" "}
                                                <span className="capitalize font-medium">{r.paymentMethod}</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-800">
                                                ₦{(r.amount - (r.discount || 0)).toLocaleString()}
                                            </p>
                                            {r.discount > 0 && (
                                                <p className="text-xs text-gray-500 line-through">
                                                    ₦{r.amount.toLocaleString()}
                                                </p>
                                            )}
                                        </div>

                                        <div className="relative">
                                            <button
                                                onClick={() => setActiveMenu(activeMenu === r.id ? null : r.id)}
                                                className="text-gray-500 hover:text-gray-700"
                                            >
                                                {FaEllipsisV({})}
                                            </button>

                                            {activeMenu === r.id && (
                                                <div
                                                    ref={(el) => (menuRefs.current[r.id] = el)}
                                                    className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg w-36 z-50"
                                                >
                                                    <button
                                                        onClick={() => {
                                                            setActiveMenu(null);
                                                            if (
                                                                validateAction(
                                                                    r.isUse,
                                                                    r.bookingType,
                                                                    "updated"
                                                                )
                                                            ) return;

                                                            // if (r.isPaid) {
                                                            const isAccess = checkAccess(
                                                                "updateBooking", access, openModal, closeModal
                                                            );
                                                            if (!isAccess) return;
                                                            setIsEdit(true);
                                                            setShowUpdateModal(true);
                                                            setBookingData({
                                                                ...r,
                                                                guestName: r.guestName,
                                                                bookingNo: `BK-${padNumber(Number(booking.bookingNo))}`,
                                                                guestNumber: r.guestNumber ?? "",
                                                            });
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                    >
                                                        {FaEdit({ size: 14 })} Update
                                                    </button>

                                                    {r.bookingType.toLocaleLowerCase() === "reservation" && <button
                                                        onClick={() => {
                                                            const checkIn = new Date(r.checkInDate.toString());
                                                            const checkOut = new Date(r.checkOutDate.toString());
                                                            // console.log(r)

                                                            confirmCheckIn(r?.roomNumber ?? "", checkIn, checkOut, () => {
                                                                checkInGuest(r.id, r.roomId)
                                                            });

                                                            setActiveMenu(null);
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                    >
                                                        {FaSignInAlt({ size: 14 })} Check In
                                                    </button>}

                                                    <button
                                                        onClick={() => {
                                                            setBookingData({
                                                                guestName: r.guestName,
                                                                roomNo: r.roomNumber,
                                                                roomId: r.roomId,
                                                                bookingId: booking.id,
                                                                roomRate: r.amount / r.duration,
                                                            });
                                                            setShowExtendModal(true);
                                                            setActiveMenu(null);
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                    >
                                                        {FaPlus({ size: 14 })} Extend Stay
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            const access = computeAccess("notifyGuest",
                                                                { guests_notify: "notifyGuest" },
                                                                { notifyGuest: false }
                                                            );

                                                            const isAccess = checkAccess(
                                                                "notifyGuest", access, openModal, closeModal
                                                            );

                                                            if (!isAccess) return;
                                                            setOpen(true);

                                                            // setShowNotifyModal(true); // 👈 open your modal
                                                            setActiveMenu(null);
                                                            setGuestName(r.guestName ?? "Guest");
                                                            setRoom({
                                                                id: r.roomId,
                                                                bookingId: r.id,
                                                                number: r?.roomNumber,
                                                                headline: r.headline ?? "",
                                                            });
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                    >
                                                        {FaBell({ size: 14 })} Notify Guest
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            const billClearBy = r.clearedBy;
                                                            if (billClearBy) {
                                                                const currentUserStr = localStorage.getItem("user");
                                                                if (!currentUserStr) {
                                                                    return openModal(
                                                                        "Session Expired ⚠️",
                                                                        "We couldn’t find your user information. Please log in again to continue.",
                                                                        () => {
                                                                            closeModal();
                                                                            window.location.href = "/login";
                                                                        },
                                                                        false,
                                                                        "Login Again"
                                                                    );
                                                                }
                                                                const currentUser = JSON.parse(currentUserStr);
                                                                const name = `${billClearBy.firstName} ${billClearBy.lastName}`;
                                                                const clearedBy =
                                                                    billClearBy.id !== currentUser.id
                                                                        ? `${name} (${billClearBy.portfolio})`
                                                                        : "You";

                                                                // ✅ Show modal if bill was already cleared
                                                                return openModal(
                                                                    "Already Cleared ⚠️",
                                                                    `This bill was already cleared by ${clearedBy}.`,
                                                                    closeModal,
                                                                    false,
                                                                    "Close"
                                                                );
                                                            }
                                                            confirmClearBill(r);
                                                            setActiveMenu(null);
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-yellow-600 hover:bg-yellow-50"
                                                    >
                                                        {FaCheck({ size: 14 })} Clear Bill
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            if (
                                                                validateAction(
                                                                    r.isUse,
                                                                    r.bookingType,
                                                                    "deleted"
                                                                )
                                                            ) return;

                                                            // if (r.isPaid) {
                                                            const isAccess = checkAccess(
                                                                "forceDeleteBooking", access, openModal, closeModal
                                                            );
                                                            if (!isAccess) return;
                                                            // }
                                                            deleteBooking(r)
                                                            setActiveMenu(null);
                                                        }}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                                    >
                                                        {FaTrash({ size: 14 })} Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="px-6 py-4 border-b text-sm text-gray-700 space-y-1">
                        <h3 className="font-medium mb-2">💰 Financial Summary</h3>
                        {((bookingCostSummary?.discount ?? 0) > 0) && <p>
                            • Gross Amount: ₦{bookingCostSummary.totalBeforeDiscount.toLocaleString()}
                        </p>}
                        {(bookingCostSummary?.discount ?? 0) > 0 && <p className="text-red-500">
                            • Discount: ₦{(bookingCostSummary.discount ?? 0).toLocaleString()}
                        </p>}
                        {
                            bookingCostSummary.totalPaid > 0 &&
                            <p>• Total Paid: ₦{(bookingCostSummary.totalPaid < 1
                                ? 0 : bookingCostSummary.totalPaid).toLocaleString()}
                            </p>
                        }
                        {!bookingCostSummary.allPaid &&
                            <p>
                                • Net Payable Amount:
                                ₦{(
                                    bookingCostSummary.totalBeforeDiscount
                                    - bookingCostSummary.totalPaid - (bookingCostSummary?.discount ?? 0)
                                ).toLocaleString()}
                            </p>
                        }

                        {/* <p>• Total Paid: ₦{(totalPaid < 1 ? 0 : totalPaid).toLocaleString()}</p> */}
                        {/* <p className="text-red-600 text-red-600">
                            • Balance: ₦{(
                                booking.totalBeforeDiscount
                                - (totalPaid < 1 ? 0 : totalPaid)
                                - (booking.discount ?? 0)
                            ).toLocaleString()}</p> */}
                    </div>

                    {/* Bill Status Badge */}
                    <div className="px-6 py-3 flex justify-center">
                        {bookingCostSummary.allPaid ? (
                            <span className="px-4 py-2 text-sm font-semibold bg-green-100 text-green-700 rounded-full border border-green-300">
                                ✅ Bill Cleared
                            </span>
                        ) : (
                            <span className="px-4 py-2 text-sm font-semibold bg-red-100 text-red-700 rounded-full border border-red-300">
                                ⚠️ Bill Unpaid
                            </span>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex flex-wrap justify-end gap-3 px-6 py-3 bg-gray-50 flex-shrink-0">
                    <button
                        onClick={
                            () => {
                                setIsEdit(false);
                                setBookingData({
                                    bookingId: booking.id,
                                    bookingNo: booking.bookingNo,
                                    guestName: booking.customer?.name,
                                    // bookingNo: generatedBillNo("BK", booking.bookingNo),
                                    // guestNumber: .guestNumber ?? "",
                                });
                                setShowUpdateModal(true);
                                // onAddRoom
                            }
                        }
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm"
                    >
                        {FaPlus({})} Add Room
                    </button>
                    <button
                        onClick={loadBookingBill}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm"
                    >
                        {FaPrint({})} Print {bookingCostSummary.allPaid ? "Receipt" : "Bill"}
                    </button>
                </div>
            </div>
        </div >
    );
};

export default BookingDetailsModal;